import { apiServiceWrapper } from '../../../../Middleware/API/apiRequest';
import { getTokensFromStorage } from '../../../../Utils/Auth/AuthToken';
import { ADMIN_LOOKUP } from '../../../../constants/apiRoutes';
import {
  getGraphqlCacheResponse,
  setGraphqlCacheResponse,
} from '../../../../Utils/Storage/IndexDB/indexDbWrappers';

/**
 * List lookups by type - Used for fetching all lookups of a specific type
 * @param {Object} params - Parameters object
 * @returns {Promise} - A promise that resolves with the list of Lookups or rejects with an error.
 */
export const listLookupByType = async ({
  moduleName,
  isServiceWorker,
  listController,
  data,
}) => {
  const { accessToken } = getTokensFromStorage();

  // Build the URL with the type parameter
  const typeParam = data?.Type || data?.dataSet?.Type || '';
  const url = `${ADMIN_LOOKUP}/GetLookupForAdmin?type=${encodeURIComponent(typeParam)}`;

  const response = await apiServiceWrapper({
    url,
    method: 'GET',
    moduleName,
    isServiceWorker,
    listController,
    accessToken,
  });

  if (response?.isError) {
    if (isServiceWorker) {
      const cacheResponse = await getGraphqlCacheResponse({}, isServiceWorker);
      return cacheResponse;
    }
    return response;
  }

  if (isServiceWorker) {
    await setGraphqlCacheResponse({}, response, isServiceWorker);
  }

  return {
    Lookups: response.Payload,
    TotalCount: response.Payload?.length,
  };
};

/**
 * List lookups by ID - Used for fetching children of a specific lookup item
 * @param {Object} params - Parameters object
 * @returns {Promise} - A promise that resolves with the list of Lookups or rejects with an error.
 */
export const listLookupById = async ({
  moduleName,
  isServiceWorker,
  listController,
  data,
}) => {
  const { accessToken } = getTokensFromStorage();

  const lookupId = data?.dataSet?.id || '';
  const url = `${ADMIN_LOOKUP}/GetLookupChildrenById?Id=${lookupId}`;

  const response = await apiServiceWrapper({
    url,
    method: 'GET',
    moduleName,
    isServiceWorker,
    listController,
    accessToken,
  });

  if (response?.isError) {
    if (isServiceWorker) {
      const cacheResponse = await getGraphqlCacheResponse({}, isServiceWorker);
      return cacheResponse;
    }
    return response;
  }

  if (isServiceWorker) {
    await setGraphqlCacheResponse({}, response, isServiceWorker);
  }

  return {
    Lookups: response.Payload,
    TotalCount: response.Payload?.length,
  };
};

/**
 * Create Lookup.
 * @returns {Promise} - A promise that resolves with the creation of lookup or rejects with an error.
 * @param data
 */
export const createLookup = async ({
  data,
  moduleName,
  isServiceWorker,
  listController,
}) => {
  const { accessToken } = getTokensFromStorage();
  const response = await apiServiceWrapper({
    url: `${ADMIN_LOOKUP}/AddLookup`,
    method: 'POST',
    moduleName,
    isServiceWorker,
    listController,
    body: data,
    accessToken,
  });
  return response;
};

/**
 * Update Lookup.
 * @returns {Promise} - A promise that resolves with the update of lookup or rejects with an error.
 * @param data
 */
export const updateLookup = async ({
  data,
  moduleName,
  isServiceWorker,
  listController,
}) => {
  const { accessToken } = getTokensFromStorage();
  const response = await apiServiceWrapper({
    url: `${ADMIN_LOOKUP}/UpdateLookup`,
    method: 'POST',
    moduleName,
    isServiceWorker,
    listController,
    body: data,
    accessToken,
  });
  return response;
};

/**
 * delete Lookup.
 * @returns {Promise} - A promise that resolves with the update of lookup or rejects with an error.
 * @param data
 */
export const deleteLookup = async ({
  data,
  moduleName,
  isServiceWorker,
  listController,
}) => {
  const { accessToken } = getTokensFromStorage();
  const response = await apiServiceWrapper({
    url: `${ADMIN_LOOKUP}/DeleteLookup?lookupId=${data?.LookupId}`,
    method: 'GET',
    moduleName,
    isServiceWorker,
    listController,
    accessToken,
  });
  return response;
};

/**
 * get Lookup.
 * @returns {Promise} - A promise that resolves with the Lookup or rejects with an error.
 * @param data
 */
export const getLookup = async ({
  data,
  moduleName,
  isServiceWorker,
  listController,
}) => {
  const { accessToken } = getTokensFromStorage();
  const response = await apiServiceWrapper({
    url: `${ADMIN_LOOKUP}/GetLookupById?lookupId=${data?.LookupId}`,
    method: 'GET',
    moduleName,
    isServiceWorker,
    listController,
    accessToken,
  });
  return response;
};
